/**
 * UserFactsProvider.ts
 *
 * Module for user facts provider.
 */

import { getDynamicSetting, SettingKey } from "@ms-ofb/officepersonalization/dist/lib/Configuration/DynamicConfiguration";
import { TULIPS_INGESTION_TIME, TULIPS_LOAD_TIMEOUT }
	from "@ms-ofb/officepersonalization/dist/lib/TargetedMessaging/TargetedMessagingConstants";
import { IUserFactProvider } from "@ms-ofb/officefloodgatecore/dist/src/Api/IUserFactProvider";
import * as AssetsProvider from "./AssetsProvider";
import * as Logging from "../Logging/Logging";

import { getCachedUserFacts } from "@ms-ofb/officepersonalization/dist/lib/Facts/CachedUserFacts";
import { IUserFact } from "@ms-ofb/officepersonalization/dist/lib/Common/Models";

import { createTimeoutPromise } from "../Utils";
import { getLocalUserFact } from "@ms-ofb/officepersonalization/dist/lib/TargetedMessaging/LocalUserFactHelper";
import * as Configuration from "../Configuration/Configuration";

export class UserFactsProvider implements IUserFactProvider {
	private loadAsyncTriggered = false;
	private userFacts: IUserFact[] = [];
	private nameAndUserFactMap: Record<string, IUserFact> = {};

	public load(): IUserFact[] {
		if (!this.loadAsyncTriggered) {
			this.loadAsync();
			this.loadAsyncTriggered = true;
		}

		return this.userFacts || [];
	}

	public loadAsync(): Promise<IUserFact[]> {
		const userFactsPromise = AssetsProvider.get().getUserFacts().then(userFacts => {
			this.loadUserFacts(userFacts);
		});

		const timeoutMilliseconds = getDynamicSetting(SettingKey.tulipsLoadTimeout, TULIPS_LOAD_TIMEOUT);
		const timeoutPromise = createTimeoutPromise(timeoutMilliseconds, userFactsPromise);
		return timeoutPromise.then(() => this.userFacts).catch(() => {
			if (!this.userFacts || this.userFacts.length <= 0) {
				this.loadUserFacts(getCachedUserFacts());
			}

			return this.userFacts;
		}).catch(() => this.userFacts);
	}

	public getUserFact(userFactName: string) {
		let userFact = this.nameAndUserFactMap[userFactName];
		if (userFact || Object.prototype.hasOwnProperty.call(this.nameAndUserFactMap, userFactName)) {
			return userFact;
		}

		userFact = this.nameAndUserFactMap[userFactName] =
			getLocalUserFact(userFactName, Configuration.get().getCommonInitOptions, Configuration.get());
		return userFact;
	}

	private loadUserFacts(response: IUserFact[]) {
		if (response) {
			this.userFacts = response || [];
			this.userFacts.map((userFact) => {
				this.nameAndUserFactMap[userFact.userFactName] = userFact;
			});
			this.loadAsyncTriggered = true;
			this.logUserFactInfo(this.userFacts);
		}
	}

	private logUserFactInfo(userFacts: IUserFact[]) {
		if (!userFacts || userFacts.length <= 0) {
			return;
		}

		const tulipsIngestionTimeInterval = getDynamicSetting(SettingKey.tulipsIngestionTimeInterval, TULIPS_INGESTION_TIME);
		if (tulipsIngestionTimeInterval <= 0) {
			return;
		}

		let userFactProperties: Logging.ICustomProperties;
		try {
			const nowDate = Date.now();
			const isExpired = (rawItemDate: string): boolean => {
				const itemDate = Date.parse(rawItemDate);
				return !isNaN(itemDate) ? nowDate - itemDate > tulipsIngestionTimeInterval : true;
			};

			const factInfo = {
				Count: userFacts.length,
				RefreshTime: tulipsIngestionTimeInterval,
				IngestionTime: userFacts[0].clientIngestionDateTime,
				SourcesExpired: userFacts.filter(userFact => userFact && isExpired(userFact.sourceDateTime)).length || undefined,
				StoragesExpired: userFacts.filter(userFact => userFact && isExpired(userFact.storageDateTime)).length || undefined,
				IngestionsExpired: userFacts.filter(userFact => userFact && isExpired(userFact.clientIngestionDateTime)).length || undefined,
			};

			userFactProperties = {
				Message: "UserFacts",
				Data: JSON.stringify(factInfo),
			};
		} catch (error) {
			userFactProperties = {
				ErrorMessage: "Error getting UserFact info",
			};
		}

		Logging.getLogger().logEvent(
			Logging.EventIds.UserFacts.Provider.Info.VALUE,
			Logging.LogLevel.Info,
			userFactProperties);
	}
}
